import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

import {
  ActivityButton,
  SequenceNumberInput,
} from "../../../components/Common";
import {
  FeatureScheduleDto,
  CreateDayScheduleItemRequest,
  UpdateFeatureRequest,
} from "../../../interfaces/control-feature";
import { ParsedError } from "../../../interfaces";
import {
  useGetFeatureQuery,
  useUpdateFeatureMutation,
  useUploadFeatureIconMutation,
} from "../../../store/api/featureApi";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../enums";
import { IMAGE_ACCEPT } from "../../../constants";

const DAYS_OF_WEEK = [
  { value: 0, labelKey: "sun" },
  { value: 1, labelKey: "mon" },
  { value: 2, labelKey: "tue" },
  { value: 3, labelKey: "wed" },
  { value: 4, labelKey: "thu" },
  { value: 5, labelKey: "fri" },
  { value: 6, labelKey: "sat" },
];

export type PerDayRow = {
  dayOfWeek: number;
  enabled: boolean;
  allDay: boolean;
  startTime: string;
  endTime: string;
};

const defaultSchedule: FeatureScheduleDto = {
  allDay: true,
  daysOfWeek: [],
  startTime: "",
  endTime: "",
  daySchedules: undefined,
};

const getEmptyInitialValues = (): UpdateFeatureRequest => ({
  title: { en: "", ar: "" },
  subTitle: { en: "", ar: "" },
  description: { en: "", ar: "" },
  unavailabilityMessage: { en: "", ar: "" },
  iconId: undefined,
  sequenceNumber: 0,
  availability: { ...defaultSchedule },
});

/** Build default 7-day schedule for "time per day" mode */
function defaultDaySchedules(): PerDayRow[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    enabled: false,
    allDay: true,
    startTime: "",
    endTime: "",
  }));
}

type AvailabilityMode = "all_day" | "same_time" | "per_day";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};

export const AddEditFeatureModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [availabilityMode, setAvailabilityMode] =
    useState<AvailabilityMode>("all_day");
  const [perDayRows, setPerDayRows] = useState<PerDayRow[]>(
    defaultDaySchedules(),
  );

  const { data: feature, error: errorGetFeature } = useGetFeatureQuery(id!, {
    skip: !id,
  });

  const [
    updateFeature,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateFeatureMutation();
  const [
    uploadIcon,
    {
      isLoading: isLoadingUploadIcon,
      data: uploadedMedia,
      isSuccess: isSuccessUploadIcon,
    },
  ] = useUploadFeatureIconMutation();

  const form = useFormik<UpdateFeatureRequest>({
    enableReinitialize: true,
    initialValues:
      id && feature?.id === id
        ? {
            title: feature.title,
            subTitle: feature.subTitle,
            description: feature.description ?? { en: "", ar: "" },
            unavailabilityMessage: feature.unavailabilityMessage ?? {
              en: "",
              ar: "",
            },
            iconId: feature.iconId ?? undefined,
            sequenceNumber: feature.sequenceNumber ?? 0,
            availability: feature.availability
              ? { ...defaultSchedule, ...feature.availability }
              : { ...defaultSchedule },
          }
        : getEmptyInitialValues(),
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      subTitle: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      description: Yup.object({
        en: Yup.string()
          .max(
            500,
            t("maxLength", { maxLength: 500, displayName: "descriptionEn" }),
          )
          .nullable(),
        ar: Yup.string()
          .max(
            500,
            t("maxLength", { maxLength: 500, displayName: "descriptionAr" }),
          )
          .nullable(),
      }),
      unavailabilityMessage: Yup.object({
        en: Yup.string()
          .max(
            200,
            t("maxLength", {
              maxLength: 200,
              displayName: "unavailabilityMessageEn",
            }),
          )
          .nullable(),
        ar: Yup.string()
          .max(
            200,
            t("maxLength", {
              maxLength: 200,
              displayName: "unavailabilityMessageAr",
            }),
          )
          .nullable(),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", { displayName: "sequenceNumber" }),
      ),
      iconId: Yup.string().required(t("isNotEmpty", { displayName: "icon" })),
      availability: Yup.object({
        allDay: Yup.boolean(),
        daysOfWeek: Yup.array().of(Yup.number().min(0).max(6)),
        daySchedules: Yup.array().optional(),
        startTime: Yup.string().when(["allDay", "daySchedules"], {
          is: (allDay: boolean, daySchedules: unknown) =>
            allDay === true ||
            (Array.isArray(daySchedules) && daySchedules.length > 0),
          then: (schema) => schema.optional(),
          otherwise: (schema) =>
            schema.required(t("isNotEmpty", { displayName: "startTime" })),
        }),
        endTime: Yup.string()
          .when(["allDay", "daySchedules"], {
            is: (allDay: boolean, daySchedules: unknown) =>
              allDay === true ||
              (Array.isArray(daySchedules) && daySchedules.length > 0),
            then: (schema) => schema.optional(),
            otherwise: (schema) =>
              schema.required(t("isNotEmpty", { displayName: "endTime" })),
          })
          .test(
            "after-start",
            t("endTimeMustBeAfterStartTime"),
            function (endTime) {
              const { allDay, startTime, daySchedules } = this.parent;
              if (Array.isArray(daySchedules) && daySchedules.length > 0)
                return true;
              if (allDay || !startTime || !endTime) return true;
              return endTime > startTime;
            },
          ),
      }),
    }),
    onSubmit: async (values) => {
      try {
        let availability: UpdateFeatureRequest["availability"];
        if (availabilityMode === "per_day") {
          const enabled = perDayRows.filter((r) => r.enabled);
          const atLeastOne = enabled.length > 0;
          if (!atLeastOne) {
            form.setFieldError("availability", t("selectAtLeastOneDay"));
            return;
          }
          const daySchedules: CreateDayScheduleItemRequest[] = enabled.map(
            (r) =>
              r.allDay
                ? { dayOfWeek: r.dayOfWeek, allDay: true }
                : {
                    dayOfWeek: r.dayOfWeek,
                    allDay: false,
                    startTime: r.startTime,
                    endTime: r.endTime,
                  },
          );
          const invalid = daySchedules.find(
            (d) =>
              !d.allDay &&
              (!d.startTime ||
                !d.endTime ||
                (d.startTime && d.endTime && d.endTime <= d.startTime)),
          );
          if (invalid) {
            form.setFieldError(
              "availability",
              t("endTimeMustBeAfterStartTime"),
            );
            return;
          }
          availability = {
            allDay: false,
            daySchedules,
          };
        } else {
          const normalizedDaysOfWeek =
            (values.availability.daysOfWeek?.length ?? 0) > 0
              ? values.availability.daysOfWeek
              : DAYS_OF_WEEK.map((day) => day.value);

          availability = {
            allDay: values.availability.allDay,
            daysOfWeek: normalizedDaysOfWeek,
            startTime: values.availability.allDay
              ? undefined
              : values.availability.startTime || undefined,
            endTime: values.availability.allDay
              ? undefined
              : values.availability.endTime || undefined,
          };
        }
        const payload: UpdateFeatureRequest = {
          title: values.title,
          subTitle: values.subTitle,
          description:
            values.description?.en || values.description?.ar
              ? values.description
              : undefined,
          unavailabilityMessage:
            values.unavailabilityMessage?.en || values.unavailabilityMessage?.ar
              ? values.unavailabilityMessage
              : undefined,
          iconId: values.iconId || undefined,
          sequenceNumber: Number(values.sequenceNumber) ?? 0,
          availability,
        };
        if (id) {
          await updateFeature({ id, data: payload }).unwrap();
        } else {
          return;
        }
        onClose();
      } catch (e) {}
    },
  });

  const onClose = () => {
    form.resetForm({ values: getEmptyInitialValues() });
    setIconPreview(null);
    setAvailabilityMode("all_day");
    setPerDayRows(defaultDaySchedules());
    onCloseClick(!show);
  };

  useEffect(() => {
    if (show) {
      form.resetForm({ values: getEmptyInitialValues() });
      setIconPreview(null);
      setAvailabilityMode("all_day");
      setPerDayRows(defaultDaySchedules());
    }
  }, [show]);

  const onChangeIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result as string);
    reader.readAsDataURL(file);
    try {
      const result = await uploadIcon({
        file,
        featureId: id || undefined,
      }).unwrap();
      if (result?.id) {
        form.setFieldValue("iconId", result.id);
        form.setFieldTouched("iconId", true);
      }
    } catch (err) {}
    e.target.value = "";
  };

  useEffect(() => {
    if (id && feature) {
      const av = feature.availability;
      form.setValues({
        title: feature.title,
        subTitle: feature.subTitle,
        description: feature.description ?? { en: "", ar: "" },
        unavailabilityMessage: feature.unavailabilityMessage ?? {
          en: "",
          ar: "",
        },
        iconId: feature.iconId ?? undefined,
        sequenceNumber: feature.sequenceNumber ?? 0,
        availability: av
          ? { ...defaultSchedule, ...av }
          : { ...defaultSchedule },
      });
      if (feature.icon?.path) {
        setIconPreview(feature.icon.path);
      } else {
        // Clear any previous preview if this feature has no icon
        setIconPreview(null);
      }
      if (av?.daySchedules && av.daySchedules.length > 0) {
        setAvailabilityMode("per_day");
        const rows = defaultDaySchedules();
        av.daySchedules.forEach((ds) => {
          const i = ds.dayOfWeek;
          if (i >= 0 && i <= 6) {
            rows[i] = {
              dayOfWeek: i,
              enabled: true,
              allDay: ds.allDay,
              startTime: ds.startTime ?? "",
              endTime: ds.endTime ?? "",
            };
          }
        });
        setPerDayRows(rows);
      } else if (av && !av.allDay) {
        setAvailabilityMode("same_time");
      } else {
        setAvailabilityMode("all_day");
      }
    }
  }, [feature, id]);

  useEffect(() => {
    if (uploadedMedia?.path) {
      setIconPreview(uploadedMedia.path);
      form.setFieldValue("iconId", uploadedMedia.id);
    }
  }, [isSuccessUploadIcon, uploadedMedia]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type === ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error as any);
    }
  }, [isLoadingUpdate, isErrorUpdate]);

  const availability = form.values.availability;

  return (
    <Modal
      id="addEditFeature"
      isOpen={show}
      toggle={onClose}
      centered
      className="modal-lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader className="p-3" toggle={onClose}>
          {id
            ? t("editItem", { item: "feature" })
            : t("addItem", { item: "feature" })}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="title.en" className="form-label">
                  {t("nameEn")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  id="title.en"
                  name="title.en"
                  value={form.values.title?.en}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(form.touched.title as any)?.en && !!form.errors.title?.en
                  }
                />
                {(form.touched.title as any)?.en && form.errors.title?.en && (
                  <FormFeedback type="invalid">
                    {form.errors.title.en as string}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="title.ar" className="form-label">
                  {t("nameAr")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  id="title.ar"
                  name="title.ar"
                  value={form.values.title?.ar}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  invalid={
                    !!(form.touched.title as any)?.ar && !!form.errors.title?.ar
                  }
                />
                {(form.touched.title as any)?.ar && form.errors.title?.ar && (
                  <FormFeedback type="invalid">
                    {form.errors.title.ar as string}
                  </FormFeedback>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="subTitle.en" className="form-label">
                  {t("subTitleEn")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  id="subTitle.en"
                  name="subTitle.en"
                  value={form.values.subTitle?.en}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(form.touched.subTitle as any)?.en &&
                    !!form.errors.subTitle?.en
                  }
                />
                {(form.touched.subTitle as any)?.en &&
                  form.errors.subTitle?.en && (
                    <FormFeedback type="invalid">
                      {form.errors.subTitle.en as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="subTitle.ar" className="form-label">
                  {t("subTitleAr")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  id="subTitle.ar"
                  name="subTitle.ar"
                  value={form.values.subTitle?.ar}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  invalid={
                    !!(form.touched.subTitle as any)?.ar &&
                    !!form.errors.subTitle?.ar
                  }
                />
                {(form.touched.subTitle as any)?.ar &&
                  form.errors.subTitle?.ar && (
                    <FormFeedback type="invalid">
                      {form.errors.subTitle.ar as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
          </Row>
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              form.touched.sequenceNumber && form.errors.sequenceNumber
                ? true
                : false
            }
            sequenceType={SequenceEnum.FEATURE}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={id ? "edit" : "add"}
            request={{
              type: SequenceEnum.FEATURE,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid" className="d-block">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="description.en" className="form-label">
                  {t("descriptionEn")}
                </Label>
                <Input
                  id="description.en"
                  name="description.en"
                  value={form.values.description?.en ?? ""}
                  type="textarea"
                  rows={3}
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(form.touched.description as any)?.en &&
                    !!(form.errors.description as any)?.en
                  }
                />
                {(form.touched.description as any)?.en &&
                  (form.errors.description as any)?.en && (
                    <FormFeedback type="invalid">
                      {(form.errors.description as any).en as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="description.ar" className="form-label">
                  {t("descriptionAr")}
                </Label>
                <Input
                  id="description.ar"
                  name="description.ar"
                  value={form.values.description?.ar ?? ""}
                  type="textarea"
                  rows={3}
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  invalid={
                    !!(form.touched.description as any)?.ar &&
                    !!(form.errors.description as any)?.ar
                  }
                />
                {(form.touched.description as any)?.ar &&
                  (form.errors.description as any)?.ar && (
                    <FormFeedback type="invalid">
                      {(form.errors.description as any).ar as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label
                  htmlFor="unavailabilityMessage.en"
                  className="form-label"
                >
                  {t("unavailabilityMessageEn")}
                </Label>
                <Input
                  id="unavailabilityMessage.en"
                  name="unavailabilityMessage.en"
                  value={form.values.unavailabilityMessage?.en ?? ""}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(form.touched.unavailabilityMessage as any)?.en &&
                    !!(form.errors.unavailabilityMessage as any)?.en
                  }
                />
                {(form.touched.unavailabilityMessage as any)?.en &&
                  (form.errors.unavailabilityMessage as any)?.en && (
                    <FormFeedback type="invalid">
                      {(form.errors.unavailabilityMessage as any).en as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label
                  htmlFor="unavailabilityMessage.ar"
                  className="form-label"
                >
                  {t("unavailabilityMessageAr")}
                </Label>
                <Input
                  id="unavailabilityMessage.ar"
                  name="unavailabilityMessage.ar"
                  value={form.values.unavailabilityMessage?.ar ?? ""}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  invalid={
                    !!(form.touched.unavailabilityMessage as any)?.ar &&
                    !!(form.errors.unavailabilityMessage as any)?.ar
                  }
                />
                {(form.touched.unavailabilityMessage as any)?.ar &&
                  (form.errors.unavailabilityMessage as any)?.ar && (
                    <FormFeedback type="invalid">
                      {(form.errors.unavailabilityMessage as any).ar as string}
                    </FormFeedback>
                  )}
              </div>
            </Col>
          </Row>

          <div className="mb-3">
            <Label className="form-label">
              {t("icon")}
              <span className="text-danger">*</span>
            </Label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="profile-user position-relative d-inline-block">
                <img
                  src={
                    iconPreview ||
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 24 24' fill='%23ddd'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E"
                  }
                  alt="icon"
                  className="rounded avatar-xl img-thumbnail"
                  style={{ objectFit: "cover", width: 80, height: 80 }}
                />
                <Label
                  htmlFor="feature_icon"
                  className="profile-photo-edit avatar-xs rounded-circle bg-light text-body border cursor-pointer"
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    cursor: "pointer",
                    padding: "0.25rem 0.5rem",
                  }}
                >
                  {isLoadingUploadIcon ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-camera-fill" />
                  )}
                </Label>
                <Input
                  id="feature_icon"
                  type="file"
                  className="d-none"
                  accept={IMAGE_ACCEPT}
                  onChange={onChangeIcon}
                  disabled={isLoadingUploadIcon}
                />
              </div>
            </div>
            {form.errors.iconId &&
            (form.touched.iconId || form.submitCount > 0) ? (
              <FormFeedback type="invalid" className="d-block">
                {form.errors.iconId as string}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label className="form-label">{t("availability")}</Label>
            <div className="mb-2">
              <div className="form-check form-check-inline">
                <Input
                  type="radio"
                  id="allDay"
                  name="availabilityType"
                  className="form-check-input"
                  checked={availabilityMode === "all_day"}
                  onChange={() => {
                    setAvailabilityMode("all_day");
                    form.setFieldValue("availability", {
                      ...availability,
                      allDay: true,
                      startTime: "",
                      endTime: "",
                    });
                  }}
                />
                <Label className="form-check-label" htmlFor="allDay">
                  {t("allDay")}
                </Label>
              </div>
              <div className="form-check form-check-inline">
                <Input
                  type="radio"
                  id="timeRange"
                  name="availabilityType"
                  className="form-check-input"
                  checked={availabilityMode === "same_time"}
                  onChange={() => {
                    setAvailabilityMode("same_time");
                    form.setFieldValue("availability", {
                      ...availability,
                      allDay: false,
                    });
                  }}
                />
                <Label className="form-check-label" htmlFor="timeRange">
                  {t("specificTime")}
                </Label>
              </div>
              <div className="form-check form-check-inline">
                <Input
                  type="radio"
                  id="perDay"
                  name="availabilityType"
                  className="form-check-input"
                  checked={availabilityMode === "per_day"}
                  onChange={() => {
                    setAvailabilityMode("per_day");
                    form.setFieldValue("availability", {
                      ...form.values.availability,
                      allDay: true,
                      daySchedules: [{}],
                      startTime: "",
                      endTime: "",
                    });
                  }}
                />
                <Label className="form-check-label" htmlFor="perDay">
                  {t("timeForEachDay")}
                </Label>
              </div>
            </div>

            {availabilityMode === "per_day" && (
              <div className="table-responsive mb-2">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("day")}</th>
                      <th className="text-center" style={{ width: 100 }}>
                        {t("available")}
                      </th>
                      <th className="text-center" style={{ width: 90 }}>
                        {t("allDay")}
                      </th>
                      <th>{t("startTime")}</th>
                      <th>{t("endTime")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DAYS_OF_WEEK.map((day) => {
                      const row = perDayRows[day.value];
                      if (!row) return null;
                      return (
                        <tr key={day.value}>
                          <td>{t(day.labelKey)}</td>
                          <td className="text-center">
                            <Input
                              type="checkbox"
                              checked={row.enabled}
                              onChange={(e) => {
                                const next = perDayRows.map((r) =>
                                  r.dayOfWeek === day.value
                                    ? { ...r, enabled: e.target.checked }
                                    : r,
                                );
                                setPerDayRows(next);
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <Input
                              type="checkbox"
                              checked={row.allDay}
                              disabled={!row.enabled}
                              onChange={(e) => {
                                const next = perDayRows.map((r) =>
                                  r.dayOfWeek === day.value
                                    ? {
                                        ...r,
                                        allDay: e.target.checked,
                                        startTime: e.target.checked
                                          ? ""
                                          : r.startTime,
                                        endTime: e.target.checked
                                          ? ""
                                          : r.endTime,
                                      }
                                    : r,
                                );
                                setPerDayRows(next);
                              }}
                            />
                          </td>
                          <td>
                            <Input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.startTime}
                              disabled={!row.enabled || row.allDay}
                              onChange={(e) => {
                                const next = perDayRows.map((r) =>
                                  r.dayOfWeek === day.value
                                    ? { ...r, startTime: e.target.value }
                                    : r,
                                );
                                setPerDayRows(next);
                              }}
                            />
                          </td>
                          <td>
                            <Input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.endTime}
                              disabled={!row.enabled || row.allDay}
                              onChange={(e) => {
                                const next = perDayRows.map((r) =>
                                  r.dayOfWeek === day.value
                                    ? { ...r, endTime: e.target.value }
                                    : r,
                                );
                                setPerDayRows(next);
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <small className="text-muted">{t("timeForEachDayHint")}</small>
              </div>
            )}

            {availabilityMode === "same_time" && !availability.allDay && (
              <Row className="g-2 mb-2">
                <Col sm={6}>
                  <Label htmlFor="startTime" className="form-label">
                    {t("startTime")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="startTime"
                    name="availability.startTime"
                    type="time"
                    className="form-control"
                    value={availability.startTime || ""}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    invalid={
                      !!(form.touched.availability as any)?.startTime &&
                      !!(form.errors.availability as any)?.startTime
                    }
                  />
                  {(form.errors.availability as any)?.startTime && (
                    <FormFeedback type="invalid">
                      {(form.errors.availability as any).startTime as string}
                    </FormFeedback>
                  )}
                </Col>
                <Col sm={6}>
                  <Label htmlFor="endTime" className="form-label">
                    {t("endTime")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="endTime"
                    name="availability.endTime"
                    type="time"
                    className="form-control"
                    value={availability.endTime || ""}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    invalid={
                      !!(form.touched.availability as any)?.endTime &&
                      !!(form.errors.availability as any)?.endTime
                    }
                  />
                  {(form.errors.availability as any)?.endTime && (
                    <FormFeedback type="invalid">
                      {(form.errors.availability as any).endTime as string}
                    </FormFeedback>
                  )}
                </Col>
              </Row>
            )}

            {availabilityMode !== "per_day" && (
              <>
                <Label className="form-label mt-2">{t("daysOfWeek")}</Label>
                <div className="d-flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <div
                      className="form-check form-check-inline"
                      key={day.value}
                    >
                      <Input
                        type="checkbox"
                        id={`day-${day.value}`}
                        className="form-check-input"
                        checked={
                          (availability.daysOfWeek || []).indexOf(day.value) >=
                          0
                        }
                        onChange={(e) => {
                          const current = availability.daysOfWeek || [];
                          const next = e.target.checked
                            ? [...current, day.value]
                            : current.filter((d) => d !== day.value);
                          form.setFieldValue("availability", {
                            ...availability,
                            daysOfWeek: next,
                          });
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor={`day-${day.value}`}
                      >
                        {t(day.labelKey)}
                      </Label>
                    </div>
                  ))}
                </div>
                <small className="text-muted">
                  {t("leaveEmptyForAllDays")}
                </small>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to="#"
            onClick={() => {
              onClose();
            }}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle" /> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingUpdate}
            disabled={
              isLoadingUpdate || !!errorGetFeature || isLoadingUploadIcon
            }
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
